import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Card, Select, WarningCallout } from "nhsuk-react-components";
import { register } from "../actions/userActions";
import { listOrganisations } from "../actions/organisationActions";
import BrowserCheck from "../components/browserCheck/BrowserCheck";

function PatientRegisterScreen({ location }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [organisationId, setOrganisationId] = useState("");
    const [errors, setErrors] = useState({});
    const [captcha, setCaptcha] = useState("");
    const [hp, setHp] = useState("");

    const dispatch = useDispatch();
    const redirect = location.search ? location.search.split("=")[1] : "/";
    const userRegister = useSelector((state) => state.userRegister);
    const { error, loading, success } = userRegister;
    const organisationList = useSelector((state) => state.organisationList);
    const { organisations, loading: loadingOrganisations, error: errorOrganisations } = organisationList;

    useEffect(() => {
        document.title = "Patient registration - MyHeartPassport";
    }, []);

    useEffect(() => {
        dispatch(listOrganisations());
    }, [dispatch]);

    useEffect(() => {
        if (organisations && organisations.length > 0 && !organisationId) {
            setOrganisationId(organisations[0].id);
        }
    }, [organisations, organisationId]);

    useEffect(() => {
        if (success) window.location.href = "/login-patient";
    }, [success]);

    const submitHandler = (e) => {
        e.preventDefault();
        const validationErrors = {};
        if (password.length < 6) validationErrors.password = "Password must be at least 6 characters long.";
        if (password !== confirmPassword) validationErrors.confirmPassword = "Passwords do not match.";
        if (!organisationId) validationErrors.organisationId = "Please select a hospital.";
        if (!email) validationErrors.email = "Please enter your email address.";
        if (!captcha) validationErrors.form = "Wait for the browser check to finish.";
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setErrors({});
        dispatch(register(email, password, organisationId, captcha, hp));
    };

    return (
        <div className="nhsuk-width-container">
            <main className="nhsuk-main-wrapper" id="maincontent" role="main">
                <div className="nhsuk-grid-row">
                    <div className="nhsuk-grid-column-two-thirds auth-card" style={{ margin: "0 auto", float: "none" }}>
                        <Card>
                            <Card.Content>
                                <header className="auth-card__header">
                                    <span className="nhsuk-caption-l">MyHeartPassport</span>
                                    <h1 className="nhsuk-heading-l">Create a patient account</h1>
                                    <p className="nhsuk-body auth-card__lede">
                                        Use the email address you want the hospital to contact. A member of staff will approve the account before you can sign in.
                                    </p>
                                </header>

                                {(error || errors.form) && (
                                    <div className="nhsuk-error-summary" role="alert" tabIndex="-1">
                                        <h2 className="nhsuk-error-summary__title">There is a problem</h2>
                                        <div className="nhsuk-error-summary__body">
                                            <ul className="nhsuk-list nhsuk-error-summary__list">
                                                <li>{errors.form || error}</li>
                                            </ul>
                                        </div>
                                    </div>
                                )}

                                <form onSubmit={submitHandler}>
                                    <div className="auth-honeypot" aria-hidden="true">
                                        <label htmlFor="patient-hp">Fax</label>
                                        <input id="patient-hp" name="hp" type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} />
                                    </div>

                                    <Select
                                        label="Hospital"
                                        disabled={loadingOrganisations}
                                        value={organisationId}
                                        onChange={(e) => setOrganisationId(e.target.value)}
                                        required
                                        error={errors.organisationId}
                                    >
                                        {loadingOrganisations ? (
                                            <Select.Option value="">Loading hospitals…</Select.Option>
                                        ) : errorOrganisations ? (
                                            <Select.Option value="">{errorOrganisations}</Select.Option>
                                        ) : (
                                            organisations && organisations.map((organisation) => (
                                                <Select.Option key={organisation.id} value={organisation.id}>{organisation.name}</Select.Option>
                                            ))
                                        )}
                                    </Select>

                                    <div className="nhsuk-form-group">
                                        <label className="nhsuk-label" htmlFor="patient-register-email">Email</label>
                                        <input className="nhsuk-input" id="patient-register-email" type="email" value={email} required onChange={(e) => setEmail(e.target.value)} aria-invalid={errors.email ? "true" : "false"} />
                                        {errors.email && <span className="nhsuk-error-message">{errors.email}</span>}
                                    </div>
                                    <div className="nhsuk-form-group">
                                        <label className="nhsuk-label" htmlFor="patient-register-password">Password</label>
                                        <input className="nhsuk-input" id="patient-register-password" type="password" value={password} required onChange={(e) => setPassword(e.target.value)} aria-invalid={errors.password ? "true" : "false"} />
                                        {errors.password && <span className="nhsuk-error-message">{errors.password}</span>}
                                    </div>
                                    <div className="nhsuk-form-group">
                                        <label className="nhsuk-label" htmlFor="patient-register-confirm">Confirm password</label>
                                        <input className="nhsuk-input" id="patient-register-confirm" type="password" value={confirmPassword} required onChange={(e) => setConfirmPassword(e.target.value)} aria-invalid={errors.confirmPassword ? "true" : "false"} />
                                        {errors.confirmPassword && <span className="nhsuk-error-message">{errors.confirmPassword}</span>}
                                    </div>

                                    <BrowserCheck onToken={setCaptcha} />

                                    <button className="nhsuk-button" type="submit" style={{ width: "100%" }} disabled={loading || !captcha}>
                                        {loading ? "Sending registration…" : "Create account"}
                                    </button>
                                </form>

                                <WarningCallout>
                                    <WarningCallout.Label>Need help?</WarningCallout.Label>
                                    <p>If you can't register, contact the team that looks after your heart device.</p>
                                </WarningCallout>

                                <p>
                                    Already registered? <Link className="nhsuk-link" to={redirect ? `/login-patient?redirect=${redirect}` : "/login-patient"}>Sign in</Link>
                                </p>
                                <p>
                                    <Link className="nhsuk-link" to="/register">Staff registration</Link>
                                </p>
                            </Card.Content>
                        </Card>
                    </div>
                </div>
            </main>
        </div>
    );
}

export default PatientRegisterScreen;
